import React from 'react';
import { SEOHead } from '../components/SEOHead';
import { Header } from '../Header';
import { EngagementGate, logEngagementView } from './EngagementGate';

// Fonts (Source Serif Pro + system sans) are loaded site-wide via public/index.html,
// so this page needs no font injection — it inherits the Real Patients brand type.
const CSS = `
.rp-engagement {
  --ink: #272726;
  --cream: #faf8f6;
  --warm-white: #f5f5f5;
  --rust: #d4796a;
  --rust-light: #e8927c;
  --gold: #f5d7aa;
  --gold-light: #faf6ec;
  --muted: #666666;
  --border: #e8e8e8;
  --card-bg: #ffffff;
  --dark-section: #272726;
  --dark-card: #353430;
  --teal: #3d7478;
  --teal-light: #eaf2f2;
  --serif: "Source Serif Pro", Georgia, serif;
  --sans: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-family: var(--sans);
  background: var(--cream);
  color: var(--ink);
  font-size: 16px;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  min-height: 100vh;
  box-sizing: border-box;
}
.rp-engagement * { margin: 0; padding: 0; box-sizing: border-box; }

/* REVEAL ANIMATIONS */
.rp-engagement .reveal { opacity: 0; transform: translateY(18px); transition: opacity 0.55s ease, transform 0.55s ease; }
.rp-engagement .reveal.visible { opacity: 1; transform: none; }

/* CORAL HIGHLIGHT SWIPE (brand signature) */
.rp-engagement .hl {
  background-image: linear-gradient(90deg, rgba(235,160,155,0.55) 0%, rgba(245,185,165,0.45) 100%);
  background-repeat: no-repeat;
  background-size: 100% 32%;
  background-position: 0 82%;
}

/* LAYOUT */
.rp-engagement .section-inner { max-width: 900px; margin: 0 auto; padding: 110px 48px; }
.rp-engagement .dark-section { background: var(--dark-section); color: var(--cream); }
.rp-engagement .warm-section { background: var(--warm-white); }
.rp-engagement hr.rule { border: none; border-top: 1px solid var(--border); margin: 0 48px; }

/* TYPE */
.rp-engagement .label { font-family: var(--sans); font-size: 11px; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase; color: var(--rust); margin-bottom: 24px; display: block; }
.rp-engagement .dark-section .label { color: var(--gold); }
.rp-engagement h1, .rp-engagement h2 { font-family: var(--serif); font-weight: 700; line-height: 1.1; letter-spacing: -0.5px; }
.rp-engagement h1 { font-size: clamp(44px, 6vw, 70px); }
.rp-engagement h2 { font-size: clamp(32px, 4vw, 50px); }
.rp-engagement .italic { font-style: italic; font-weight: 700; }
.rp-engagement p.lead { font-size: 18px; line-height: 1.68; color: #4a453e; margin-top: 24px; font-weight: 400; }
.rp-engagement p.lead strong { font-weight: 600; color: var(--ink); }
.rp-engagement .dark-section p.lead { color: #c4bfb7; }

/* HERO */
.rp-engagement .hero-wrap { position: relative; overflow: hidden; }
.rp-engagement .hero-wrap::before {
  content: ''; position: absolute; top: -260px; left: 50%; transform: translateX(-50%);
  width: 1100px; height: 720px; max-width: 150vw;
  background: radial-gradient(ellipse, rgba(235,160,155,0.22) 0%, transparent 65%);
  pointer-events: none; z-index: 0;
}
.rp-engagement .hero-inner { max-width: 900px; margin: 0 auto; padding: 148px 48px 100px; position: relative; z-index: 1; }
.rp-engagement .hero-stat-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; background: var(--border); border: 1px solid var(--border); margin-top: 64px; }
.rp-engagement .hero-stat { background: var(--card-bg); padding: 30px; text-align: center; }
.rp-engagement .hero-stat-num { font-family: var(--serif); font-weight: 700; font-size: 38px; color: var(--ink); letter-spacing: -0.5px; display: block; }
.rp-engagement .hero-stat-label { font-size: 12px; font-weight: 400; color: var(--muted); margin-top: 6px; display: block; }

/* CALLOUT — matches the /intelligence gradient pull-quote boxes */
.rp-engagement .inline-note {
  background: linear-gradient(135deg, rgba(250,190,192,0.40) 0%, rgba(245,215,170,0.32) 50%, rgba(181,201,168,0.28) 100%);
  border: 1px solid rgba(235,160,155,0.25);
  border-radius: 10px;
  padding: 30px 40px;
  margin: 44px auto 0;
  max-width: 720px;
  font-family: var(--serif);
  font-style: italic;
  font-size: 18px;
  line-height: 1.6;
  font-weight: 500;
  color: var(--ink);
  text-align: center;
}
.rp-engagement .inline-note strong { font-weight: 700; font-style: italic; color: var(--ink); }

/* WHO WE ARE */
.rp-engagement .pillar-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; background: var(--border); border: 1px solid var(--border); margin-top: 48px; }
.rp-engagement .pillar { background: var(--card-bg); padding: 34px 26px; }
.rp-engagement .pillar-name { font-family: var(--sans); font-size: 10px; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase; color: var(--rust); margin-bottom: 10px; display: block; }
.rp-engagement .pillar h3 { font-family: var(--serif); font-size: 20px; font-weight: 700; margin-bottom: 10px; }
.rp-engagement .pillar p { font-size: 13px; color: var(--muted); line-height: 1.6; font-weight: 400; }

/* RECRUITMENT */
.rp-engagement .recruit-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1px; background: var(--border); border: 1px solid var(--border); margin-top: 48px; }
.rp-engagement .recruit-card { background: var(--card-bg); padding: 32px 28px; }
.rp-engagement .recruit-card h3 { font-family: var(--serif); font-size: 19px; font-weight: 700; margin-bottom: 8px; }
.rp-engagement .recruit-card p { font-size: 13px; color: var(--muted); line-height: 1.6; font-weight: 400; }
.rp-engagement .recruit-stat { font-family: var(--serif); font-size: 36px; font-weight: 700; color: var(--rust); letter-spacing: -0.5px; display: block; margin-bottom: 6px; }

/* OPS */
.rp-engagement .ops-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; background: var(--border); border: 1px solid var(--border); margin-top: 48px; }
.rp-engagement .ops-card { background: var(--card-bg); padding: 28px 24px; }
.rp-engagement .ops-icon { display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 10px; background: rgba(235,160,155,0.14); color: var(--rust); margin-bottom: 16px; transition: background 0.2s; }
.rp-engagement .ops-icon svg { display: block; }
.rp-engagement .ops-card:hover .ops-icon { background: rgba(235,160,155,0.26); }
.rp-engagement .ops-card h3 { font-family: var(--serif); font-size: 17px; font-weight: 700; margin-bottom: 6px; }
.rp-engagement .ops-card p { font-size: 13px; color: var(--muted); line-height: 1.55; font-weight: 400; }

/* PLATFORM */
.rp-engagement .platform-split { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 48px; align-items: stretch; }
.rp-engagement .platform-card { background: var(--card-bg); border: 1px solid var(--border); border-radius: 12px; box-shadow: 0 4px 24px rgba(0,0,0,0.04); padding: 32px 32px 36px; text-align: left; }
.rp-engagement .platform-head { display: flex; align-items: center; gap: 14px; margin-bottom: 16px; }
.rp-engagement .platform-chip { display: inline-flex; align-items: center; justify-content: center; width: 42px; height: 42px; border-radius: 10px; flex-shrink: 0; }
.rp-engagement .platform-chip svg { display: block; }
.rp-engagement .platform-card h3 { font-family: var(--serif); font-size: 20px; font-weight: 700; margin: 0; }
.rp-engagement .platform-card > p { font-size: 14px; color: var(--muted); line-height: 1.6; font-weight: 400; margin-bottom: 18px; }
.rp-engagement .platform-card ul { padding-left: 0; list-style: none; display: flex; flex-direction: column; gap: 13px; }
.rp-engagement .platform-card ul li { font-size: 14px; color: #4a453e; line-height: 1.5; font-weight: 400; display: flex; gap: 12px; align-items: flex-start; }
.rp-engagement .platform-card ul li svg { flex-shrink: 0; margin-top: 1px; }
.rp-engagement .platform-card.provide .platform-chip { background: rgba(235,160,155,0.16); color: var(--rust); }
.rp-engagement .platform-card.provide ul li svg { color: var(--rust); }
.rp-engagement .platform-card.protect .platform-chip { background: rgba(61,116,120,0.12); color: var(--teal); }
.rp-engagement .platform-card.protect ul li svg { color: var(--teal); }

/* METHODS */
.rp-engagement .method-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1px; background: var(--border); border: 1px solid var(--border); margin-top: 48px; }
.rp-engagement .method-card { background: var(--card-bg); padding: 30px; }
.rp-engagement .method-mode { font-family: var(--sans); font-size: 10px; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase; color: var(--rust); margin-bottom: 8px; display: inline-flex; align-items: center; gap: 8px; }
@keyframes rp-pulse {
  0% { box-shadow: 0 0 0 0 rgba(232,146,124,0.55); }
  70% { box-shadow: 0 0 0 10px rgba(232,146,124,0); }
  100% { box-shadow: 0 0 0 0 rgba(232,146,124,0); }
}
.rp-engagement .mode-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--rust-light); display: inline-block; flex-shrink: 0; }
.rp-engagement .live-dot { box-shadow: 0 0 0 0 rgba(232,146,124,0.55); animation: rp-pulse 2s infinite; }
.rp-engagement .mode-lines { display: inline-flex; flex-direction: column; gap: 2px; }
.rp-engagement .mode-lines span { height: 2px; border-radius: 1px; background: var(--rust); display: block; }
.rp-engagement .mode-lines span:nth-child(1) { width: 12px; }
.rp-engagement .mode-lines span:nth-child(2) { width: 8px; }
.rp-engagement .mode-lines span:nth-child(3) { width: 10px; }
.rp-engagement .mode-combo { display: inline-flex; align-items: center; gap: 4px; }
.rp-engagement .mode-combo .mode-dot { width: 6px; height: 6px; }
.rp-engagement .mode-div { width: 1px; height: 8px; background: var(--rust-light); display: inline-block; }
.rp-engagement .mode-lines-sm span { height: 1.5px; }
.rp-engagement .mode-lines-sm span:nth-child(1) { width: 8px; }
.rp-engagement .mode-lines-sm span:nth-child(2) { width: 6px; }
.rp-engagement .method-card h3 { font-family: var(--serif); font-size: 19px; font-weight: 700; margin-bottom: 8px; }
.rp-engagement .method-card p { font-size: 13px; color: var(--muted); line-height: 1.55; font-weight: 400; }
.rp-engagement .method-tags { margin-top: 10px; font-size: 11px; color: #a8a29a; font-style: italic; }

/* HOW WE WORK TOGETHER */
.rp-engagement .model-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; background: var(--border); border: 1px solid var(--border); margin-top: 48px; }
.rp-engagement .model-card { background: var(--card-bg); padding: 30px 26px; }
.rp-engagement .model-num { font-family: var(--serif); font-size: 42px; font-weight: 700; color: var(--border); display: block; margin-bottom: 8px; line-height: 1; }
.rp-engagement .model-card h3 { font-family: var(--serif); font-size: 18px; font-weight: 700; margin-bottom: 8px; }
.rp-engagement .model-card p { font-size: 13px; color: var(--muted); line-height: 1.6; font-weight: 400; }
.rp-engagement .model-card.highlight { background: #fdf3f0; border-top: 3px solid var(--rust); }
.rp-engagement .model-card.highlight .model-num { color: #f0cfc6; }

/* Q&A TABLE — /intelligence comparison-table feel */
.rp-engagement .boundary-wrap { margin-top: 48px; background: var(--card-bg); border: 1px solid var(--border); border-radius: 12px; box-shadow: 0 4px 24px rgba(0,0,0,0.04); overflow-x: auto; }
.rp-engagement .boundary-table { width: 100%; border-collapse: separate; border-spacing: 0; font-family: var(--sans); font-size: 14px; min-width: 600px; }
.rp-engagement .boundary-table th { padding: 16px 20px; text-align: left; font-size: 11px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--muted); background: var(--warm-white); border-bottom: 1px solid var(--border); }
.rp-engagement .boundary-table td { padding: 18px 20px; text-align: left; vertical-align: top; line-height: 1.6; font-weight: 400; color: #4a453e; border-bottom: 1px solid var(--border); }
.rp-engagement .boundary-table td:first-child { font-weight: 600; color: var(--ink); width: 38%; }
.rp-engagement .boundary-table tbody tr:last-child td { border-bottom: none; }
.rp-engagement .boundary-table tbody tr:hover td { background: rgba(235,160,155,0.05); }
.rp-engagement .tag-yes { display: inline-block; background: #e6f4f2; color: #2a7a6e; font-size: 10px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; padding: 3px 8px; border-radius: 3px; margin-bottom: 8px; }
.rp-engagement .tag-no { display: inline-block; background: #faece9; color: #c0402a; font-size: 10px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; padding: 3px 8px; border-radius: 3px; margin-bottom: 8px; }
.rp-engagement .tag-depends { display: inline-block; background: #fbf2e2; color: #b07d28; font-size: 10px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; padding: 3px 8px; border-radius: 3px; margin-bottom: 8px; }

/* CTA */
.rp-engagement .cta-section { background: var(--dark-section); color: var(--cream); padding: 100px 48px; text-align: center; }
.rp-engagement .cta-section h2 { font-family: var(--serif); font-size: clamp(34px, 4.5vw, 54px); font-weight: 700; letter-spacing: -0.5px; line-height: 1.12; color: var(--cream); }
.rp-engagement .cta-section p { font-size: 17px; color: #c4bfb7; font-weight: 400; margin-top: 20px; max-width: 520px; margin-left: auto; margin-right: auto; line-height: 1.65; }
.rp-engagement .cta-pill { display: inline-block; margin-top: 36px; background: var(--rust-light); color: #fff; font-size: 14px; font-weight: 600; letter-spacing: 0.02em; padding: 15px 30px; border-radius: 8px; text-decoration: none; transition: background 0.2s; }
.rp-engagement .cta-pill:hover { background: var(--rust); }
.rp-engagement .cta-sub { margin-top: 18px; font-size: 12px; color: #9a958c; }

/* FOOTER */
.rp-engagement footer { background: var(--dark-section); color: #8a8278; padding: 36px 48px; border-top: 1px solid #3a3935; display: flex; justify-content: space-between; align-items: flex-end; font-size: 12px; font-weight: 400; }
.rp-engagement .footer-logo { font-family: var(--serif); font-weight: 700; font-size: 15px; color: #b0aa9f; }

@media (max-width: 700px) {
  .rp-engagement .section-inner, .rp-engagement .hero-inner { padding: 80px 24px; }
  .rp-engagement h1 { font-size: 38px; }
  .rp-engagement h2 { font-size: 28px; }
  .rp-engagement .hero-stat-row, .rp-engagement .pillar-row, .rp-engagement .ops-grid, .rp-engagement .method-grid, .rp-engagement .model-row { grid-template-columns: 1fr; }
  .rp-engagement .recruit-grid, .rp-engagement .platform-split { grid-template-columns: 1fr; }
  .rp-engagement footer { flex-direction: column; gap: 12px; }
}
`;

const useReveal = () => {
  React.useEffect(() => {
    const root = document.documentElement;
    const prevBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'smooth';

    const els = document.querySelectorAll('.rp-engagement .reveal');
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.07, rootMargin: '0px 0px -36px 0px' }
    );
    els.forEach((el) => obs.observe(el));

    return () => {
      obs.disconnect();
      root.style.scrollBehavior = prevBehavior;
    };
  }, []);
};

// Mode indicator for method cards — pulsing dot (Live), stacked lines (Async),
// or a combo (Live or Async). Mirrors the /intelligence GoDeeperSection badge.
const ModeBadge = ({ mode }) => {
  const isLive = mode === 'Live';
  const isAsync = mode === 'Async';
  return (
    <span className="method-mode">
      {isLive && <span aria-hidden="true" className="mode-dot live-dot" />}
      {isAsync && (
        <span aria-hidden="true" className="mode-lines"><span /><span /><span /></span>
      )}
      {!isLive && !isAsync && (
        <span aria-hidden="true" className="mode-combo">
          <span className="mode-dot" />
          <span className="mode-div" />
          <span className="mode-lines mode-lines-sm"><span /><span /></span>
        </span>
      )}
      {mode}
    </span>
  );
};

// Custom monoline icons for the ops cards — brand line-icon style (no stock emoji).
const ICON = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.75, strokeLinecap: 'round', strokeLinejoin: 'round' };
const IconRecruit = () => (
  <svg {...ICON}><circle cx="9" cy="8" r="3.2" /><path d="M3.5 20a5.5 5.5 0 0 1 11 0" /><path d="M18 7v6M15 10h6" /></svg>
);
const IconConsent = () => (
  <svg {...ICON}><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 3h6v3H9z" /><path d="M9 13l2 2 4-4" /></svg>
);
const IconSchedule = () => (
  <svg {...ICON}><rect x="4" y="5" width="16" height="16" rx="2" /><path d="M4 10h16M8 3v4M16 3v4" /><circle cx="9" cy="14.5" r="1.1" /></svg>
);
const IconChat = () => (
  <svg {...ICON}><path d="M4 5h16a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H9l-4 3v-3H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" /><path d="M8.5 10.5h.01M12 10.5h.01M15.5 10.5h.01" /></svg>
);
const IconCard = () => (
  <svg {...ICON}><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M3 10h18M7 14.5h3" /></svg>
);
const IconNodes = () => (
  <svg {...ICON}><circle cx="6" cy="6" r="2.2" /><circle cx="18" cy="6" r="2.2" /><circle cx="12" cy="18" r="2.2" /><path d="M8.2 6h7.6M7.6 7.8l3 8.4M16.4 7.8l-3 8.4" /></svg>
);
const IconLayers = () => (
  <svg {...ICON}><path d="M12 3l9 5-9 5-9-5 9-5z" /><path d="M3 13l9 5 9-5" /></svg>
);
const IconShield = () => (
  <svg {...ICON}><path d="M12 3l7 3v5c0 4.6-3 7.6-7 9-4-1.4-7-4.4-7-9V6l7-3z" /><path d="M9 12l2 2 4-4" /></svg>
);
const CHECK = { width: 17, height: 17, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const CheckMark = () => (
  <svg {...CHECK}><circle cx="12" cy="12" r="9" /><path d="M8.5 12.2l2.4 2.4 4.6-5" /></svg>
);

export const EngagementPage = ({ bannerOffset = 0, headerProps = {} }) => {
  useReveal();
  React.useEffect(() => {
    logEngagementView();
  }, []);

  return (
    <>
      <Header {...headerProps} bannerOffset={bannerOffset} />
      <div className="rp-engagement">
      <SEOHead
        title="Working With Us"
        description="How Real Patients works with life sciences teams, agencies, and research partners — rigorous recruitment, end-to-end participant management, and patient-centered research methods."
        url="https://www.realpatients.com/engagement"
        noIndex={true}
      />
      <style>{CSS}</style>

      {/* HERO */}
      <div className="hero-wrap">
        <div className="hero-inner" style={{ paddingTop: `${bannerOffset + 218}px` }}>
          <h1 className="reveal">Working with<br /><span className="italic">Real <span className="hl">Patients.</span></span></h1>
          <p className="lead reveal">Real Patients helps life sciences organizations and their partners access verified patient and caregiver insights. Our model is built around rigorous recruitment, end-to-end participant management, and patient-centered research — designed to deliver high-quality insights while protecting the community that makes them possible.</p>

          <div className="hero-stat-row reveal">
            <div className="hero-stat">
              <span className="hero-stat-num">10 Years</span>
              <span className="hero-stat-label">Leading patient engagement</span>
            </div>
            <div className="hero-stat">
              <span className="hero-stat-num">2,500+</span>
              <span className="hero-stat-label">Conditions represented</span>
            </div>
            <div className="hero-stat">
              <span className="hero-stat-num">Global</span>
              <span className="hero-stat-label">Reach across patient communities</span>
            </div>
          </div>
        </div>
      </div>

      <hr className="rule" />

      {/* WHO WE ARE */}
      <div id="who-we-are" className="warm-section">
        <div className="section-inner">
          <span className="label reveal">Who We Are</span>
          <h2 className="reveal">Patient-founded.<br /><span className="italic">Patient-led. Always.</span></h2>
          <p className="lead reveal">Real Patients was founded by patients and built around a single purpose: ensuring that real patient experience informs the decisions that affect patients most. We connect organizations with verified patients and caregivers to generate high-quality, trustworthy insights — across conditions, care stages, and research objectives.</p>

          <div className="pillar-row reveal">
            <div className="pillar">
              <span className="pillar-name">Our Community</span>
              <h3>Verified across 2,500+ conditions</h3>
              <p>An active, growing community spanning common chronic conditions to rare diseases — across diverse geographies, demographics, and care pathways.</p>
            </div>
            <div className="pillar">
              <span className="pillar-name">Our Model</span>
              <h3>Built on long-term relationships</h3>
              <p>We prioritize sustained patient relationships over transactional engagement. The quality and depth of our community reflects a decade of consistent investment in trust.</p>
            </div>
            <div className="pillar">
              <span className="pillar-name">Our Purpose</span>
              <h3>Patient experience in every decision</h3>
              <p>From drug development to clinical trials to communications, our mission is to bring real patient experience into the decisions that shape healthcare. That purpose informs how we work with every partner.</p>
            </div>
          </div>
        </div>
      </div>

      <hr className="rule" />

      {/* HOW WE RECRUIT */}
      <div id="how-we-recruit">
        <div className="section-inner">
          <span className="label reveal">How We Recruit</span>
          <h2 className="reveal">Not a panel.<br /><span className="italic">A peer-to-peer network.</span></h2>
          <p className="lead reveal">Our community grows through trusted referrals — patients learning about Real Patients through others in their condition communities. This peer-to-peer model produces stronger engagement and more authentic participation than traditional panel recruitment. Every member is verified before participating in any research.</p>

          <div className="recruit-grid reveal">
            <div className="recruit-card">
              <span className="recruit-stat">Peer-to-peer</span>
              <h3>Community-driven growth</h3>
              <p>We actively cultivate relationships within disease-specific networks, patient organizations, and condition communities — and patients find us through trusted referrals from others with shared lived experience. Both sides of that dynamic shape the quality of every engagement.</p>
            </div>
            <div className="recruit-card">
              <span className="recruit-stat">Verified</span>
              <h3>Condition and identity confirmed</h3>
              <p>Every community member completes a multi-factor verification process before participating in any research. Identity and diagnosis are confirmed, ensuring data integrity from the start.</p>
            </div>
            <div className="recruit-card">
              <span className="recruit-stat">Matched</span>
              <h3>Targeted to your research needs</h3>
              <p>We recruit participants matched to your research objectives — working with you to define the right criteria and confirm feasibility before scoping begins.</p>
            </div>
            <div className="recruit-card">
              <span className="recruit-stat">Supported</span>
              <h3>Patients who are engaged and prepared</h3>
              <p>Participants are compensated fairly, informed thoroughly, and supported throughout each engagement. Our commitment to the patient experience is what sustains a community willing to contribute their time and perspective.</p>
            </div>
          </div>
        </div>
      </div>

      <hr className="rule" />

      {/* END-TO-END ENGAGEMENT */}
      <div id="engagement" className="warm-section">
        <div className="section-inner">
          <span className="label reveal">How We Manage Engagements</span>
          <h2 className="reveal">Patient engagement,<br /><span className="italic">managed end to end.</span></h2>
          <p className="lead reveal">Real Patients manages the full participant experience for every engagement — from recruitment through compensation. This structure ensures consistent quality, protects patient privacy, and reduces operational burden for your team.</p>

          <div className="ops-grid reveal">
            <div className="ops-card">
              <span className="ops-icon"><IconRecruit /></span>
              <h3>Recruitment</h3>
              <p>We activate existing community members and recruit new participants matched to your research needs — screening to the criteria that matter most for your project.</p>
            </div>
            <div className="ops-card">
              <span className="ops-icon"><IconConsent /></span>
              <h3>Consent Management</h3>
              <p>All participants are consented prior to engagement using our standard consent form. When a project requires an IRB-approved form or additional release language, we accommodate that — we're flexible on the process while consistent on the requirement.</p>
            </div>
            <div className="ops-card">
              <span className="ops-icon"><IconSchedule /></span>
              <h3>Scheduling</h3>
              <p>We manage all participant scheduling — coordinating across time zones, care routines, and availability. Reminders and confirmations are handled by our team, so participants are prepared and sessions run on time.</p>
            </div>
            <div className="ops-card">
              <span className="ops-icon"><IconChat /></span>
              <h3>Participant Communication</h3>
              <p>All participant-facing communication flows through Real Patients — from initial outreach through post-engagement follow-up. One consistent, trusted voice.</p>
            </div>
            <div className="ops-card">
              <span className="ops-icon"><IconCard /></span>
              <h3>Compensation</h3>
              <p>Participants receive fair market value compensation, paid directly and promptly by Real Patients. Participants are not waiting on third-party processing — payment is managed by us and delivered quickly after each engagement.</p>
            </div>
            <div className="ops-card">
              <span className="ops-icon"><IconNodes /></span>
              <h3>Project Coordination</h3>
              <p>We manage timelines, protocol alignment, and quality control from kick-off to delivery — with one point of contact for your team throughout.</p>
            </div>
          </div>

          <div className="inline-note reveal">
            <strong>Why this structure matters:</strong> Consistent patient management means better engagement and more reliable data. Real Patients maintains a <span className="hl">95% participant completion rate</span> across engagements — a direct reflection of how we support participants from first contact through final session.
          </div>
        </div>
      </div>

      <hr className="rule" />

      {/* PLATFORM & DATA */}
      <div id="platform">
        <div className="section-inner">
          <span className="label reveal">Platform & Data</span>
          <h2 className="reveal">Engagements live on<br /><span className="italic">the Real Patients platform.</span></h2>
          <p className="lead reveal">All research engagements are hosted on the Real Patients platform. This structure protects patient privacy, maintains data integrity, and ensures a consistent, compliant experience for participants across every project.</p>

          <div className="platform-split reveal">
            <div className="platform-card provide">
              <div className="platform-head">
                <span className="platform-chip"><IconLayers /></span>
                <h3>What we provide</h3>
              </div>
              <p>Deliverables are scoped to the project and may include:</p>
              <ul>
                <li><CheckMark />Recordings and transcripts for live sessions</li>
                <li><CheckMark />Structured participant responses for asynchronous engagements</li>
                <li><CheckMark />Reporting and analysis as scoped to the research objectives</li>
              </ul>
            </div>
            <div className="platform-card protect">
              <div className="platform-head">
                <span className="platform-chip"><IconShield /></span>
                <h3>What we protect</h3>
              </div>
              <p>Patient privacy is foundational to how we operate.</p>
              <ul>
                <li><CheckMark />No patient PII is shared with external parties</li>
                <li><CheckMark />Participants are not transferred off our platform</li>
                <li><CheckMark />All data handling is compliant and audit-ready</li>
              </ul>
            </div>
          </div>

          <div className="inline-note reveal">
            <strong>On patient data:</strong> We do not share patient PII, and all engagements are governed by <span className="hl">strict data use agreements</span>. This is a condition of all partnerships — it is how we maintain the trust and integrity of our community.
          </div>
        </div>
      </div>

      <hr className="rule" />

      {/* METHODS */}
      <div id="methods" className="warm-section">
        <div className="section-inner">
          <span className="label reveal">Our Research Methods</span>
          <h2 className="reveal">The right method<br /><span className="italic">for the right question.</span></h2>
          <p className="lead reveal">We support a full range of qualitative and mixed-methods research approaches. Whether you need live conversations or asynchronous activities, small exploratory samples or larger cohorts — we design the methodology to fit the objective.</p>

          <div className="method-grid reveal">
            <div className="method-card">
              <ModeBadge mode="Live" />
              <h3>In-Depth Interviews</h3>
              <p>One-on-one conversations surfacing rich lived experience and the nuanced language patients use to describe their condition, treatment, and care journey. Facilitated by our research team or yours — on our platform.</p>
              <p className="method-tags">Lived experience · unmet needs · exploratory conversations</p>
            </div>
            <div className="method-card">
              <ModeBadge mode="Live" />
              <h3>Focus Groups & Advisory Boards</h3>
              <p>Group discussions surfacing shared experiences, areas of divergence, and collective patient priorities. End-to-end management available, or patient recruitment and hosting for your facilitation — on our platform.</p>
              <p className="method-tags">Community norms · comparative perspectives · co-design</p>
            </div>
            <div className="method-card">
              <ModeBadge mode="Async" />
              <h3>Activity Boards</h3>
              <p>Structured asynchronous platform activities capturing qualitative depth at scale — on participants' own schedules, without requiring synchronous availability. Well-suited for concept testing, material review, and longitudinal engagement.</p>
              <p className="method-tags">Concept validation · patient materials · longitudinal engagement</p>
            </div>
            <div className="method-card">
              <ModeBadge mode="Async" />
              <h3>Protocol Reviews</h3>
              <p>Structured patient review of trial design, burden of participation, and informed consent — flagging barriers and informing design before recruitment begins.</p>
              <p className="method-tags">De-risking trial design · inclusion criteria · identifying barriers</p>
            </div>
            <div className="method-card">
              <ModeBadge mode="Async" />
              <h3>Surveys</h3>
              <p>Quantitative and qualitative surveys designed for larger cohorts — statistically meaningful at scale, with verified participants throughout. Designed for your objectives, not off-the-shelf.</p>
              <p className="method-tags">Population-level insight · PRO testing · pattern analysis</p>
            </div>
            <div className="method-card">
              <ModeBadge mode="Live or Async" />
              <h3>Concept & Materials Review</h3>
              <p>Patients review communications, educational content, clinical materials, or prototypes and provide structured, actionable feedback. Works equally well live or asynchronous depending on depth needed.</p>
              <p className="method-tags">Message testing · patient materials · prototype feedback</p>
            </div>
          </div>
        </div>
      </div>

      <hr className="rule" />

      {/* WORKING TOGETHER */}
      <div id="working-together">
        <div className="section-inner">
          <span className="label reveal">Working With Us</span>
          <h2 className="reveal">Two ways to<br /><span className="italic">engage with us.</span></h2>
          <p className="lead reveal">Most partnerships fall into one of two models — choose whichever fits your needs. We're here to support you end-to-end, or simply where you need us most.</p>

          <div className="model-row reveal" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="model-card">
              <span className="model-num">01</span>
              <h3>Recruitment only</h3>
              <p>Your team or a designated facilitator conducts the research. Real Patients manages the full participant lifecycle — recruitment, verification, consent, scheduling, communication, and compensation — with all sessions hosted on our platform.</p>
            </div>
            <div className="model-card highlight">
              <span className="model-num">02</span>
              <h3>Recruitment and research</h3>
              <p>Real Patients manages everything: methodology design, recruitment, participant management, discussion guide development, facilitation, analysis, and reporting. You receive a finished research deliverable — ready to act on. This is our most common engagement model.</p>
            </div>
          </div>

          <div className="inline-note reveal">
            <strong>Consistent across both models:</strong> Participants <span className="hl">remain on the Real Patients platform</span>. Patient PII is not shared, and participants are not transferred to third-party systems. Research deliverables — transcripts, recordings, data outputs — are provided to your team as scoped.
          </div>
        </div>
      </div>

      <hr className="rule" />

      {/* WHAT TO EXPECT TABLE */}
      <div className="warm-section">
        <div className="section-inner">
          <span className="label reveal">Common Questions</span>
          <h2 className="reveal">Questions we address<br /><span className="italic">at the start of every partnership.</span></h2>
          <p className="lead reveal">The following reflects what partners typically want to understand before scoping begins. If something isn't covered here, we're happy to address it directly.</p>

          <div className="boundary-wrap reveal">
          <table className="boundary-table">
            <thead>
              <tr>
                <th>Question</th>
                <th>Our answer</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><span className="tag-yes">Yes</span><br />Can you help determine the right research methodology?</td>
                <td>Yes. When we're conducting the research, methodology design is part of what we do. We work with your team to define the right approach based on the research objective, timeline, and participant profile — and we bring recommendations, not just execution.</td>
              </tr>
              <tr>
                <td><span className="tag-yes">Yes</span><br />Do you write discussion guides and research materials?</td>
                <td>Yes. Discussion guide development, screener design, and participant-facing materials are included when Real Patients is conducting the research. We develop these in close collaboration with your team to ensure alignment with the project objectives.</td>
              </tr>
              <tr>
                <td><span className="tag-yes">Yes</span><br />Can you recruit to a very specific patient profile?</td>
                <td>Yes. We recruit by condition, treatment stage, care pathway, geography, demographics, caregiver status, and more. For highly specific profiles, we assess feasibility during scoping and are transparent about timelines and limitations.</td>
              </tr>
              <tr>
                <td><span className="tag-yes">Yes</span><br />Can we facilitate the sessions ourselves?</td>
                <td>Yes. We can support partner-led facilitation when sessions are hosted on the Real Patients platform. This ensures patient privacy and data quality are maintained regardless of who is conducting the research.</td>
              </tr>
              <tr>
                <td><span className="tag-yes">Yes</span><br />Will we receive transcripts and recordings?</td>
                <td>Yes. For live sessions, partners receive recordings and full transcripts. For asynchronous engagements, structured data outputs are provided. Deliverable format is scoped to your project requirements.</td>
              </tr>
              <tr>
                <td><span className="tag-depends">Depends</span><br />Can you support international recruitment?</td>
                <td>Our community spans multiple geographies. Reach and feasibility vary by condition area and location — we assess this during scoping and provide a clear picture of what is achievable for your specific project.</td>
              </tr>
              <tr>
                <td><span className="tag-no">No</span><br />Can you share participant contact information?</td>
                <td>No. Patient PII is not shared with external parties under any circumstances. All participant communication is managed by Real Patients. Research outputs — transcripts, recordings, and structured data — are delivered with participant information blinded or de-identified.</td>
              </tr>
              <tr>
                <td><span className="tag-no">No</span><br />Can participants be moved to a third-party platform?</td>
                <td>No. All engagements are hosted on the Real Patients platform. Participants are not transferred to external tools or vendor systems. This is a standard condition of all partnerships.</td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="cta-section" id="contact">
        <h2 className="reveal">Ready to talk through<br /><span className="italic">a specific project?</span></h2>
        <p className="reveal">Reach out directly or tell us about your research needs and we'll be in touch.</p>
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '36px' }}>
          <a href="mailto:sbd@realpatients.com" className="cta-pill reveal" style={{ marginTop: 0 }}>Get in Touch →</a>
          <a
            href="https://share.hsforms.com/1TP2ARRY4Qnm8Y9ia6iTZDA37h64"
            target="_blank"
            rel="noopener noreferrer"
            className="cta-pill reveal"
            style={{ marginTop: 0, background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', color: '#c4bfb7' }}
          >
            Request Insights →
          </a>
        </div>
        <p className="cta-sub reveal">sbd@realpatients.com</p>
      </div>

      <footer>
        <div>
          <div className="footer-logo">Real Patients™</div>
          <div style={{ marginTop: '6px', fontSize: '11px', color: '#8a8278' }}>Formerly Savvy Cooperative · Est. 2016 · Patient-founded. Patient-led.</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div>sbd@realpatients.com</div>
          <div style={{ marginTop: '4px' }}>© 2026 Real Patients</div>
        </div>
      </footer>
      </div>
      <EngagementGate />
    </>
  );
};

export default EngagementPage;
